/**
 * One paper of an exam — created or edited in a dialog.
 *
 * The papers used to be edited inline in the page's own table, which was the
 * right shape while a paper was four short fields. Chapters are not that: a
 * class-10 syllabus is thirty of them across several subjects, and a picker for
 * that cannot live in a table cell. So the row became a row — read it, or open
 * it — and everything that describes one paper is here.
 *
 * Save WRITES. A dialog whose Save button only stages is a thing a reader is
 * entitled to disbelieve, and an exam is built one paper at a time over days —
 * each is its own piece of work and survives closing the tab.
 *
 * One exception, and it is not a choice: an exam that does not exist yet has
 * nothing to add a paper TO. Its papers stay with the page and go up when its
 * own Save mints the exam, and the button says "Add paper" rather than "Save"
 * so it does not promise otherwise.
 */
import { useEffect, useState } from 'react';
import { Button, DatePicker, FormField, Input, Modal, MultiSelectFilter } from '@shared-ui/components';
import { useSubjectLabel } from '@shared-ui/reference-data';
import type { ExamPaper } from '@shared-ui/class-shared';
import { useExamChapters } from './useExamChapters';

export interface ExamPaperDialogProps {
  /** The paper being edited, or null to create one. */
  paper: ExamPaper | null;
  isOpen: boolean;
  subjectOptions: { value: string; label: string }[];
  /** The class's board and grade — what a subject's curriculum resolves from. */
  board?: string;
  grade?: string;
  onSave: (paper: ExamPaper) => void;
  onClose: () => void;
  /** True while the write is in flight, so Save can say so and not fire twice. */
  saving?: boolean;
  /** Whether saving WRITES. False for an exam that does not exist yet, whose
   *  papers go up with it — the button then says what it actually does. */
  persists?: boolean;
}

const blank = (): ExamPaper => ({ id: '', subjects: [], date: '' });

export function ExamPaperDialog({
  paper, isOpen, subjectOptions, board, grade, onSave, onClose, saving, persists = true,
}: ExamPaperDialogProps) {
  const subjectLabel = useSubjectLabel();
  const [draft, setDraft] = useState<ExamPaper>(paper ?? blank());
  const [error, setError] = useState<string | null>(null);

  // Reseed whenever the dialog opens on a different paper — a dialog that kept
  // the last paper's chapters would quietly copy them onto the next one.
  useEffect(() => {
    if (isOpen) { setDraft(paper ?? blank()); setError(null); }
  }, [isOpen, paper]);

  const labelFor = (subjects: string[]) => subjects.map(subjectLabel).join(', ');

  const set = (patch: Partial<ExamPaper>) => setDraft(d => {
    const next = { ...d, ...patch };
    // Same rule as the page had: the label follows the subjects until someone
    // writes their own, and then it is theirs.
    if (patch.subjects && (!d.label || d.label === labelFor(d.subjects))) {
      next.label = labelFor(patch.subjects) || undefined;
    }
    return next;
  });

  const { groups, allIds: allChapterIds, isLoading: chaptersLoading } =
    useExamChapters(draft.subjects, board, grade, isOpen);

  /* ABSENT scope means the whole curriculum — NOT an empty array, which would
     mean a paper examining nothing (see ExamPaper.scope). So "everything" is
     the absence of a list, and the checkboxes only appear once it is narrowed. */
  const wholeSyllabus = draft.scope === undefined;

  const toggleChapter = (id: string) => setDraft(d => {
    const current = d.scope ?? allChapterIds;
    const next = current.includes(id) ? current.filter(x => x !== id) : [...current, id];
    return { ...d, scope: next };
  });

  const submit = () => {
    if (draft.subjects.length === 0) { setError('Pick at least one subject.'); return; }
    if (!draft.date) { setError('The paper needs a date.'); return; }
    if (draft.scope && draft.scope.length === 0) {
      setError('A paper examining no chapters is not a paper — pick some, or set it to the whole syllabus.');
      return;
    }
    onSave(draft);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={paper ? 'Edit paper' : 'Add a paper'}
      size="lg"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : persists ? 'Save' : 'Add paper'}
          </Button>
        </>
      )}
    >
      <div className="exam-paper-dialog">
        <FormField label="Subjects" required>
          <MultiSelectFilter
            label="Subjects"
            options={subjectOptions}
            selected={draft.subjects}
            onChange={next => set({ subjects: next })}
          />
        </FormField>

        <div className="school-field-row">
          <FormField label="Date" required>
            {/* The shared picker, not `input[type=date]` — the native control
                brings its own typography and its own calendar, which is the one
                thing in this dialog that would not look like the app. */}
            <DatePicker value={draft.date} onChange={iso => set({ date: iso })} ariaLabel="Paper date" />
          </FormField>
          <FormField label="Max marks">
            <Input
              type="number"
              value={draft.maxMarks != null ? String(draft.maxMarks) : ''}
              placeholder="—"
              onChange={e => set({ maxMarks: e.target.value ? Number(e.target.value) : undefined })}
            />
          </FormField>
        </div>

        <FormField label="Label">
          <Input
            value={draft.label ?? ''}
            placeholder={labelFor(draft.subjects) || 'Theory / Paper 2'}
            onChange={e => set({ label: e.target.value || undefined })}
          />
        </FormField>

        <FormField label="Chapters examined">

          {draft.subjects.length === 0 && (
            <p className="school-hint">Pick a subject first — its chapters are what there is to choose from.</p>
          )}

          {draft.subjects.length > 0 && chaptersLoading && (
            <p className="school-hint">Loading chapters…</p>
          )}

          {draft.subjects.length > 0 && !chaptersLoading && allChapterIds.length === 0 && (
            <p className="school-hint">
              No curriculum found for {draft.subjects.map(subjectLabel).join(', ')}
              {board ? ` on ${board}` : ''}{grade ? ` grade ${grade}` : ''}. The paper will
              cover the whole syllabus.
            </p>
          )}

          {allChapterIds.length > 0 && (
            <>
              <label className="exam-scope-all">
                <input
                  type="checkbox"
                  checked={wholeSyllabus}
                  onChange={e => set({ scope: e.target.checked ? undefined : allChapterIds })}
                />
                <span>The whole syllabus</span>
              </label>

              {!wholeSyllabus && groups.map(t => (
                <div key={t.subject} className="exam-scope-group">
                  <div className="exam-scope-subject">{subjectLabel(t.subject)}</div>
                  {t.chapters.map(c => (
                    <label key={c.id} className="exam-scope-chapter">
                      <input
                        type="checkbox"
                        checked={(draft.scope ?? allChapterIds).includes(c.id)}
                        onChange={() => toggleChapter(c.id)}
                      />
                      <span>{c.name}</span>
                    </label>
                  ))}
                </div>
              ))}
            </>
          )}
        </FormField>

        {error && <p className="school-error">{error}</p>}
      </div>
    </Modal>
  );
}
